import { HeroIcons } from '../../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import useLocale from '../../hooks/useLocale'
import { getLocaleAppBaseUrl } from '../../lib/utils/language'
import gtmService from '../../services/gtm.service'

interface AnimatedStat {
  target: number
  label: string
  suffix?: string
  prefix?: string
  decimals?: number
  icon: typeof HeroIcons.UserGroupIcon
}

const statBase: Omit<AnimatedStat, 'label'>[] = [
  {
    target: 500,
    suffix: '+',
    icon: HeroIcons.UserGroupIcon,
  },
  {
    target: 35,
    suffix: 'K €',
    icon: HeroIcons.CurrencyEuroIcon,
  },
  {
    target: 3,
    suffix: '+',
    icon: HeroIcons.ShieldCheckIcon,
  },
]

const formatStat = (value: number, stat: AnimatedStat) => {
  const decimals = stat.decimals ?? 0
  const rounded =
    decimals > 0 ? Number(value.toFixed(decimals)) : Math.round(value)

  const localized = rounded.toLocaleString('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

  return `${stat.prefix || ''}${localized}${stat.suffix || ''}`
}

export default function ProofSection() {
  const { t } = useTranslation('index')
  const { locale } = useLocale()
  const sectionRef = useRef<HTMLElement | null>(null)
  const hasAnimatedRef = useRef(false)
  const [isInView, setIsInView] = useState(false)
  const statLabels = t('landing.proof.stats', {
    returnObjects: true,
  }) as Array<{ label: string }>
  const stats = statBase.map((stat, index) => ({
    ...stat,
    label: statLabels[index]?.label || '',
  }))
  const [animatedValues, setAnimatedValues] = useState<number[]>(
    statBase.map(() => 0),
  )

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry.isIntersecting || hasAnimatedRef.current) return

        hasAnimatedRef.current = true
        setIsInView(true)
        observer.disconnect()
      },
      { threshold: 0.35 },
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isInView) return

    const duration = 1300
    const stepDelay = 120
    const totalDuration = duration + stepDelay * (stats.length - 1)
    let animationFrameId = 0
    let startTime = 0

    const update = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const elapsed = timestamp - startTime

      const nextValues = stats.map((stat, index) => {
        const localElapsed = Math.min(
          Math.max(elapsed - index * stepDelay, 0),
          duration,
        )
        const progress = localElapsed / duration
        const eased = 1 - Math.pow(1 - progress, 3)
        return stat.target * eased
      })

      setAnimatedValues(nextValues)

      if (elapsed < totalDuration) {
        animationFrameId = requestAnimationFrame(update)
      } else {
        setAnimatedValues(stats.map((stat) => stat.target))
      }
    }

    animationFrameId = requestAnimationFrame(update)

    return () => cancelAnimationFrame(animationFrameId)
  }, [isInView])

  return (
    <section
      ref={sectionRef}
      className="relative flex w-full items-center justify-center"
    >
      <img
        src="/img/ornaments/plant-building.png"
        alt=""
        aria-hidden
        className="pointer-events-none absolute -right-28 top-32 hidden w-[360px] max-w-none xl:block 2xl:w-[570px]"
      />

      <div className="mx-auto grid w-full max-w-[820px] gap-9 px-3 tablet:gap-12 tablet:px-10 desktop:max-w-none desktop:grid-cols-[minmax(0,560px)_minmax(0,1fr)] desktop:gap-44 desktop:px-44">
        <div className="desktop:order-2 desktop:flex desktop:w-max desktop:grow desktop:flex-col">
          <div className="w-full px-3 tablet:px-0">
            <h2 className="font-semibold text-neutral-900 text-shelters-section-title">
              {t('landing.proof.title.line1')}
              <br />
              {t('landing.proof.title.line2Prefix')}
              <span className="text-sheltersBlue">
                {t('landing.proof.title.highlight')}
              </span>
            </h2>

            <p className="mt-5 max-w-xl text-neutral-700 text-shelters-section-body">
              {t('landing.proof.description')}
            </p>
            <a
              href={getLocaleAppBaseUrl(locale, 'properties')}
              onClick={() =>
                gtmService.ctaClicked({
                  ctaId: 'proof_properties',
                  ctaLabel: t('landing.proof.cta'),
                  ctaLocation: 'proof_section',
                  destinationUrl: getLocaleAppBaseUrl(locale, 'properties'),
                  destinationType: 'app_properties',
                  language: locale,
                })
              }
              className="mt-7 inline-flex items-center border-b border-sheltersBlue pb-1 text-shelters-kicker font-medium text-sheltersBlue desktop:inline-block desktop:border-neutral-900 desktop:text-neutral-900"
            >
              {t('landing.proof.cta')}
              <span className="ml-2 text-[1.25rem] leading-none desktop:hidden">
                →
              </span>
            </a>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 tablet:grid-cols-3 tablet:gap-4 lg:mt-auto desktop:mt-10 desktop:gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon

              return (
                <div
                  key={stat.label}
                  className={`flex items-center gap-4 rounded-card border border-[#dbe2ff] bg-white/90 p-4 shadow-[0_12px_28px_rgba(51,40,154,0.08)] backdrop-blur-sm transition-all duration-700 tablet:flex-col tablet:items-start desktop:block desktop:border-0 desktop:bg-transparent desktop:p-0 desktop:shadow-none desktop:backdrop-blur-0 ${
                    isInView
                      ? 'translate-y-0 opacity-100'
                      : 'translate-y-2 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 120}ms` }}
                >
                  <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient text-white shadow-[0_10px_24px_rgba(51,40,154,0.24)] desktop:hidden">
                    <Icon className="h-7 w-7" />
                  </span>

                  <div>
                    <p
                      className={`font-medium text-sheltersBlue transition-transform duration-700 text-shelters-stat-fluid ${
                        isInView ? 'scale-100' : 'scale-90'
                      }`}
                      style={{ transitionDelay: `${160 + index * 120}ms` }}
                    >
                      {formatStat(animatedValues[index], stat)}
                    </p>
                    <p className="mt-1 text-shelters-kicker text-sheltersDarkBlue desktop:mt-2">
                      {stat.label}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div
          data-navtheme="frosted"
          className="relative aspect-[1.06] w-full overflow-hidden rounded-container bg-[url('/img/ornaments/tokenization.png')] bg-cover bg-center bg-no-repeat shadow-[0_18px_42px_rgba(51,40,154,0.16)] desktop:order-1 desktop:mx-auto desktop:aspect-square desktop:max-w-[560px] desktop:rounded-card desktop:shadow-none"
        >
          <div className="absolute bottom-4 left-4 right-4 rounded-inner-card border border-white/25 bg-black/25 p-4 text-white backdrop-blur-md tablet:bottom-5 tablet:left-5 tablet:right-auto tablet:p-5">
            <div className="mb-3">
              <p className="text-shelters-kicker">
                {t('landing.proof.imageCard.title')}
              </p>
            </div>

            <div className="flex gap-7 tablet:gap-12">
              <div>
                <p className="font-medium text-shelters-stat-fluid">520K</p>
                <p className="mt-1 text-shelters-kicker text-white/85">
                  {t('landing.proof.imageCard.collected')}
                </p>
              </div>
              <div>
                <p className="font-medium text-shelters-stat-fluid">3</p>
                <p className="mt-1 text-shelters-kicker text-white/85">
                  {t('landing.proof.imageCard.projects')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
